type RouteParam = string | number;
type RouteBuilder = (id?: RouteParam) => string;

interface AppRoutes {
  preAlertReport: string;
  preAlertReportView: string;
  preAlertF5View: string;
  saleOrder: string;
  saleOrderView: string;
  saleOrderCheck: string;
  saleOrderEdit: RouteBuilder;
  saleOrderDrafts: string;
  bookingDraftForm: RouteBuilder;
  saleOrderLegacy: string;
  saleOrderCheckLegacy: string;
  saleOrderLegacyPlural: string;
  saleInvoice: string;
  saleInvoiceView: string;
  saleInvoiceEdit: RouteBuilder;
  saleInvoicePush: RouteBuilder;
  planning: string;
  planningView: string;
  planningAiCheck: string;
  planningEdit: RouteBuilder;
  planningLegacy: string;
  planningLegacyView: string;
  planningLegacyEdit: RouteBuilder;
  vesselPlanning: string;
  vesselPlanningList: string;
  vesselPlanningView: RouteBuilder;
  vesselPlanningLegacy: string;
  rti: string;
  rtiView: string;
  rtiEdit: RouteBuilder;
  employeeAssignments: string;
  rtiLegacy: string;
  employeeMaster: string;
  employeeMasterView: string;
  employeeMasterEdit: RouteBuilder;
  bills: string;
  billsView: string;
  billsNew: string;
  billsEdit: RouteBuilder;
  paymentVoucher: string;
  paymentVoucherView: string;
  paymentVoucherEdit: RouteBuilder;

  /** Accounts payable desk (role 1100). */
  payableDashboard: string;
  /** Supplier bill register, served by the legacy /BillMaster path. */
  billMaster: string;
  /** Pay-bills screen. */
  payment: string;
  /** Purchase orders awaiting verification before they can be billed. */
  purchaseOrderCheck: string;
  /** Payment vouchers already settled. */
  paymentVoucherCompleted: string;
  /** Petty cash book. Legacy spells the action "PetteyCash". */
  pettyCash: string;
  /** Petty cash list / view page. */
  pettyCashView: string;
  /** Driver RTI salary report. */
  driverSalaryReport: string;
  /** AI provider routing for document reading. Admin only (roles 100 / 200). */
  aiSettings: string;
  /** Per-role access to controlled screens (Planning). Super Admin only. */
  screenAccess: string;
  /** Unread mail per company mailbox, live. Super Admin only. */
  mailMonitor: string;
  /** Mailbox list, people, limits and passwords for the monitor. Super Admin only. */
  mailMonitorSettings: string;
  /** Reply times per mailbox: within target, slowest, waiting, AI summary. Super Admin only. */
  mailResponseReport: string;
  /** Mail received and sent between 7 pm and 8 am, per night and mailbox. Super Admin only. */
  mailAfterHours: string;

  /** Fuel entry: list, blank form, and one entry by id. */
  fuelEntry: string;
  fuelEntryNew: string;
  fuelEntryEdit: RouteBuilder;

  /** Toll entries: list, blank form, and one entry by id. */
  tollEntry: string;
  tollEntryNew: string;
  tollEntryEdit: RouteBuilder;

  /** Levi entries: list, blank form, and one entry by id. */
  leviEntry: string;
  leviEntryNew: string;
  leviEntryEdit: RouteBuilder;

  /** Auto pass entries: list, blank form, and one entry by id. */
  autoPassEntry: string;
  autoPassEntryNew: string;
  autoPassEntryEdit: RouteBuilder;

  /** Incident reports: list, blank form, and one report by id. */
  irReport: string;
  irReportNew: string;
  irReportEdit: RouteBuilder;

  /** GPS readings captured from Wialon. Read-only screens. */
  gpsEngineHours: string;
  gpsSpeedReport: string;
  gpsFuelFillings: string;
  // Quick Access Navigation Routes
  customerServiceDB: string;
  customerService: string;
  customerServiceEmployee: (employeeId: number | string) => string;
  invoiceCheck: string;
  quotation: string;
  quotationView: string;
  wanInventory: string;
  otherInventory: string;
  saleJobView: string;
  boardingJobView: string;
  googleReview: string;
  staffReview: string;
  weeklyReport: string;
  mailInbox: string;
  transportDashboard: string;
  transportOngoingJobView: string;
  driverComments: string;
  /** Spare entry, served by the legacy /PurchaseMaster path. */
  spareEntry: string;
  truckMaster: string;
  driverMaster: string;
  customerMaster: string;
  truckMasterView: string;
  /** The Truck Order Calendar. Path kept from the legacy screen so the dashboard tiles that already link here keep working. */
  truckOrderCalendar: string;
  /** The weekly Truck Location Board. A new screen, so the path is its own. */
  truckLocationBoard: string;
  truckMaintenance: string;
  truckMaintenanceDashboard: string;
  purchaseOrder: string;
  purchaseOrderPush: RouteBuilder;
  receipt: string;
  receiptView: string;
  customerStatement: string;
  customerStatementSend: string;
  customerStatementConversation: (customerId?: number | string) => string;
  saleEmployeeView: string;
  salePortView: string;
  addressReport: string;
  agentReport: string;
  customerReport: string;
  driverReport: string;
  employeeReport: string;
  itemReport: string;
  productReport: string;
  supplierReport: string;
  saleReport: string;
  saleDetailedReport: string;
  saleCreditReport: string;
  saleCreditDetailedReport: string;
  customerBalanceReport: string;
  customerAgeingReport: string;
  customerReceiptReport: string;
  customerReceiptDueReport: string;
  customerQuotationReport: string;
  supplierBalanceReport: string;
  supplierPaymentReport: string;
  supplierPaymentDueReport: string;
  supplierStatementReport: string;
  purchaseReport: string;
  purchaseDetailedReport: string;
  purchaseItemWiseReport: string;
  billsReport: string;
  billsDetailedReport: string;
  billsItemWiseReport: string;
  expenseReport: string;
  paymentVoucherReport: string;
  salaryReport: string;
  fuelReport: string;
  renewalReport: string;
  vesselActivityReport: string;
  missingRemarksReport: string;
  forwardingReport: string;
  forwardingDashboard: string;
  saleOrderForwardingReport: string;
  saleOrderForwardingReportLegacy: string;
  /** The forwarding team's planning list of Customer Service's forwarding requests. */
  forwardingRequests: string;
  /** A CS employee's own forwarding requests, read-only, with their status. */
  myForwardingRequests: string;
  zbEntry: string;
  zbEntryLegacy: string;
  zbEntryListLegacy: string;
  accountsReceivable: string;
  accountsReceivableLegacy: string;
  employeeDashboard: string;
  hrDashboard: string;
  hrAdminDashboard: string;
  jobOrderNew: string;
  jobOrderEdit: string;
  jobOrderView: string;
  inventory: string;
  inventoryNew: string;
  inventoryDetail: RouteBuilder;
  inventoryEdit: RouteBuilder;
  recon: string;
  reconDetail: RouteBuilder;
}

export const APP_ROUTES: AppRoutes = {
  preAlertReport: "/Report/PreAlertRerport",
  preAlertReportView: "/Report/PreAlertRerport/view",
  preAlertF5View: "/Report/PreAlertRerport/prealert-view",
  saleOrder: "/sale-order",
  saleOrderView: "/sale-order/view",
  saleOrderCheck: "/sale-order/check",
  saleOrderEdit: (id = ":id") => `/sale-order/edit/${id}`,
  /** Bookings that arrived by e-mail, waiting to become sale orders. */
  saleOrderDrafts: "/sale-order/drafts",
  /** The Booking Draft form: check a booking mail's draft and create the sale order from it. */
  bookingDraftForm: (draftId = ":draftId") => `/sale-order/drafts/${draftId}`,
  saleOrderLegacy: "/SaleOrder",
  saleOrderCheckLegacy: "/SaleOrder/SaleOrderCheck",
  saleOrderLegacyPlural: "/sales-order",
  saleInvoice: "/sale-invoice",
  saleInvoiceView: "/sale-invoice/view",
  saleInvoiceEdit: (id = ":id") => `/sale-invoice/edit/${id}`,
  // A new invoice pre-loaded with one job, opened by the sale order's Push
  // Invoice button. The job travels as a path segment, not a query string:
  // the router shim re-serialises search values and an unrecognised one can
  // be rewritten or dropped before the screen ever reads it.
  saleInvoicePush: (saleOrderId = ":saleOrderId") => `/sale-invoice/push/${saleOrderId}`,
  planning: "/planning",
  planningView: "/planning/view",
  planningAiCheck: "/planning/ai-check",
  planningEdit: (id = ":id") => `/planning/edit/${id}`,
  planningLegacy: "/PLANING",
  planningLegacyView: "/PLANING/view",
  planningLegacyEdit: (id = ":id") => `/PLANING/edit/${id}`,
  vesselPlanning: "/vessel-planning",
  vesselPlanningList: "/vessel-planning/list",
  vesselPlanningView: (id = ":id") => `/vessel-planning/view/${id}`,
  vesselPlanningLegacy: "/VESSELPLANING",
  rti: "/rti",
  rtiView: "/rti/view",
  rtiEdit: (id = ":id") => `/rti/edit/${id}`,
  employeeAssignments: "/employee-assignments",
  rtiLegacy: "/RTI",
  employeeMaster: "/EmployeeMaster",
  employeeMasterView: "/EmployeeMaster/EmployeeView",
  employeeMasterEdit: (id = ":id") => `/EmployeeMaster?Id=${id}`,
  bills: "/bills",
  billsView: "/bills/view",
  billsNew: "/bills/new",
  billsEdit: (id = ":id") => `/bills/${id}`,
  paymentVoucher: "/PaymentVoucher",
  paymentVoucherView: "/PaymentVoucher/View",
  paymentVoucherEdit: (id = ":id") => `/PaymentVoucher/Edit/${id}`,

  payableDashboard: "/dashboard/payable",
  billMaster: "/BillMaster",
  payment: "/Payment",
  purchaseOrderCheck: "/BillsOrderMaster/BillsOrderCheck",
  paymentVoucherCompleted: "/PaymentVoucher/PaymentCompleted",
  pettyCash: "/PettyCash/PetteyCash",
  pettyCashView: "/PettyCash/View",
  driverSalaryReport: "/Report/DriverRTIReport",
  aiSettings: "/AiSettings",
  screenAccess: "/screen-access",
  mailMonitor: "/mail-monitor",
  mailMonitorSettings: "/mail-monitor/settings",
  mailResponseReport: "/mail-monitor/response-report",
  mailAfterHours: "/mail-monitor/after-hours",

  fuelEntry: "/FuelEntry",
  fuelEntryNew: "/FuelEntry/new",
  fuelEntryEdit: (id = ":id") => `/FuelEntry/${id}`,

  tollEntry: "/TollEntry",
  tollEntryNew: "/TollEntry/new",
  tollEntryEdit: (id = ":id") => `/TollEntry/${id}`,

  leviEntry: "/LeviEntry",
  leviEntryNew: "/LeviEntry/new",
  leviEntryEdit: (id = ":id") => `/LeviEntry/${id}`,

  autoPassEntry: "/AutoPassEntry",
  autoPassEntryNew: "/AutoPassEntry/new",
  autoPassEntryEdit: (id = ":id") => `/AutoPassEntry/${id}`,

  irReport: "/IRReport",
  irReportNew: "/IRReport/new",
  irReportEdit: (id = ":id") => `/IRReport/${id}`,

  gpsEngineHours: "/GPSEntry/EngineHours",
  gpsSpeedReport: "/GPSEntry/SpeedReport",
  gpsFuelFillings: "/GPSEntry/FuelFillings",
  // Quick Access Navigation Routes
  customerServiceDB: "/dashboard/customerservicedb",
  customerService: "/dashboard/customerservice",
  customerServiceEmployee: (employeeId: number | string) => `/dashboard/customerservice/employee/${employeeId}`,
  invoiceCheck: "/invoice/check",
  quotation: "/quotation",
  quotationView: "/quotation/view",
  wanInventory: "/inventory/wan-hai",
  otherInventory: "/inventory/other",
  saleJobView: "/sale-job/view",
  boardingJobView: "/boarding-job/view",
  googleReview: "/reviews/google",
  staffReview: "/reviews/staff",
  weeklyReport: "/reports/weekly",
  mailInbox: "/mail/inbox",
  transportDashboard: "/transport-dashboard",
  transportOngoingJobView: "/transport-ongoing-job/view",
  driverComments: "/driver-comments",
  spareEntry: "/PurchaseMaster",
  truckMaster: "/TruckMaster",
  driverMaster: "/DriverMaster",
  customerMaster: "/CustomerMaster",
  truckMasterView: "/TruckMaster/TruckView",
  truckOrderCalendar: "/TruckMaster/TruckOrderTimetable",
  truckLocationBoard: "/truck-location-board",
  truckMaintenance: "/truck-maintenance",
  truckMaintenanceDashboard: "/truck-maintenance-dashboard",
  purchaseOrder: "/BillsOrderMaster",
  // A new purchase order pre-loaded with one sale order, opened by that
  // order's Push PO button. A path segment rather than a query string or a
  // localStorage key, for the reasons on saleInvoicePush above - and because a
  // shared key is state any tab can leave behind for the next push to pick up.
  purchaseOrderPush: (saleOrderId = ":saleOrderId") => `/BillsOrderMaster/push/${saleOrderId}`,
  /** Customer receipt entry (Spring /api/receipts). Legacy path kept: the menu and bookmarks point here. */
  receipt: "/Receipt",
  /** RECEIPT ENTRY VIEW, which the legacy page opened as an F5 popup. */
  receiptView: "/Receipt/View",
  /** Customer Statement of Account. Legacy path kept: the menu and bookmarks point here. */
  customerStatement: "/Report/CustomerStatement",
  /** Send Statements workspace: pick customers, check, mail them all in one run. */
  customerStatementSend: "/Report/CustomerStatement/Send",
  /** The workspace with one customer's conversation (their replies, our answers) already open. */
  customerStatementConversation: (customerId: number | string = ":customerId") => `/Report/CustomerStatement/Send/conversation/${customerId}`,
  saleEmployeeView: "/Report/SaleEmployeeView",
  salePortView: "/Report/SalePortView",
  // The Report menu's filter-and-View reports (one page, ReportFilterPage). Legacy paths kept: the menu points here.
  addressReport: "/Report/AddressReport",
  agentReport: "/Report/AgentReport",
  customerReport: "/Report/CustomerReport",
  driverReport: "/Report/DriverReport",
  employeeReport: "/Report/EmployeeReport",
  itemReport: "/Report/ItemReport",
  productReport: "/Report/ProductReport",
  supplierReport: "/Report/SupplierReport",
  saleReport: "/Report/SaleReport",
  saleDetailedReport: "/Report/SaleDetailedReport",
  saleCreditReport: "/Report/SaleCreditReport",
  saleCreditDetailedReport: "/Report/SaleCreditDetailedReport",
  customerBalanceReport: "/Report/CustomerBalance",
  customerAgeingReport: "/Report/CustomerAgingReport",
  customerReceiptReport: "/Report/CustomerReceipt",
  customerReceiptDueReport: "/Report/CustomerReceiptDue",
  customerQuotationReport: "/Report/CustomerQuotationReport",
  supplierBalanceReport: "/Report/SupplierBalance",
  supplierPaymentReport: "/Report/SupplierPayment",
  supplierPaymentDueReport: "/Report/SupplierPaymentDue",
  supplierStatementReport: "/Report/SupplierStatement",
  purchaseReport: "/Report/PurchaseReport",
  purchaseDetailedReport: "/Report/PurchaseDetailedReport",
  purchaseItemWiseReport: "/Report/PurchaseItemWiseReport",
  billsReport: "/Report/BillsReport",
  billsDetailedReport: "/Report/BillsDetailedReport",
  billsItemWiseReport: "/Report/BillItemWiseReport",
  expenseReport: "/Report/ExpenseReport",
  paymentVoucherReport: "/Report/PaymentVoucherReport",
  salaryReport: "/Report/SalaryReport",
  fuelReport: "/Report/FuelReport",
  renewalReport: "/Report/RenewalReport",
  vesselActivityReport: "/Report/VesselActivityReport",
  missingRemarksReport: "/Report/MissingRemarksReport",
  forwardingReport: "/reports/forwardingplanning",
  forwardingDashboard: "/dashboard/forwarding",
  // The sale order forwarding report — legacy `Report/SaleOrderFWReport`.
  // Not to be confused with `forwardingReport` above, which is the planning
  // report (lorry, driver, ETA) and a different screen entirely.
  saleOrderForwardingReport: "/reports/sale-order-forwarding",
  saleOrderForwardingReportLegacy: "/Report/SaleOrderFWReport",
  forwardingRequests: "/forwarding/requests",
  myForwardingRequests: "/forwarding/my-requests",
  // ZB entry (port / ZB / PTW charges), legacy `Billing/ZBVIEW`.
  zbEntry: "/zb-entry",
  zbEntryLegacy: "/Billing/zbview",
  zbEntryListLegacy: "/Billing/ZBALLVIEW",
  // Accounts receivable desk, legacy `DashBoard/AccountReceivableDB`.
  accountsReceivable: "/accounts-receivable",
  accountsReceivableLegacy: "/DashBoard/AccountReceivableDB",
  employeeDashboard: "/dashboard/employee",
  hrDashboard: "/dashboard/hr",
  hrAdminDashboard: "/dashboard/hradmin",
  jobOrderNew: "/job-order/new",
  jobOrderEdit: "/job-order/edit/:id",
  jobOrderView: "/job-order/view",
  inventory: "/Inventory",
  inventoryNew: "/Inventory/new",
  inventoryDetail: (id = ":id") => `/Inventory/${id}`,
  inventoryEdit: (id = ":id") => `/Inventory/${id}/edit`,
  recon: "/Recon",
  reconDetail: (id = ":id") => `/Recon/${id}`,
};

export default APP_ROUTES;
